/**
 * System API Service
 * Handles all public/system API calls that don't require authentication
 * Migrated from helpers/system_api_helper.ts
 */

import { APIClient } from "./api.service";
import * as url from "../constants/urls";
import logger from "../utils/logger";

const api = new APIClient();

/**
 * Organization Branding Response Interface
 */
export interface OrganizationBranding {
  organizationId: number | null;
  org_name: string;
  subdomain?: string;
  logo?: string;
  banner_image?: string;
  favicon?: string;
  favicon_text?: string;
  login_screen_images?: {
    image1?: string;
    image2?: string;
    image3?: string;
    [key: string]: string | undefined;
  };
  branding_colors?: {
    primary?: string;
    secondary?: string;
  };
  welcome_message?: string;
  status?: string;
  custom_domain?: string | null;
  // Social Media URLs
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  youtube_url?: string;
  // Company URLs
  about_us_url?: string;
  contact_us_url?: string;
  privacy_policy_url?: string;
  // App Store URLs
  play_store_url?: string;
  app_store_url?: string;
  // Access Control Fields (for blocked/suspended organizations)
  is_accessible?: boolean;
  access_blocked?: boolean;
  block_reason?: 'not_found' | 'suspended' | 'inactive' | 'trial_expired' | 'subscription_expired' | string;
  block_title?: string;
  block_message?: string;
  contact_message?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_website?: string;
  // Legacy field names for backward compatibility
  organizationName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  welcomeMessage?: string;
  loginBackgroundImage?: string;
  supportEmail?: string;
  supportPhone?: string;
  address?: string;
  [key: string]: any;
}

/**
 * Default Basiq360 Branding
 * Used as fallback when organization-branding API fails
 */
export const DEFAULT_BRANDING: OrganizationBranding = {
  organizationId: 1,
  org_name: "Basiq360 CRM",
  subdomain: "",
  logo: process.env.PUBLIC_URL + "/logo.png",
  branding_colors: {
    primary: "#2563eb", // Blue-600
    secondary: "#1d4ed8", // Blue-700
  },
  welcome_message: "Enterprise Customer Relationship Management - Transform Your Business",
  status: "active",
  // Legacy fields for backward compatibility
  organizationName: "Basiq360 CRM",
  primaryColor: "#2563eb",
  secondaryColor: "#1d4ed8",
  welcomeMessage: "Enterprise Customer Relationship Management - Transform Your Business",
  loginBackgroundImage: "",
  supportEmail: "support@basiq360.com",
  supportPhone: "+1-800-BASIQ360",
};

/**
 * Get Organization Branding by Subdomain or Hostname
 * This is a public API that doesn't require authentication
 *
 * @param subdomain - Optional subdomain (if not provided, will be extracted from window.location)
 * @returns Organization branding details including organizationId
 */
export const getOrganizationBranding = async (
  subdomain?: string
): Promise<{
    statusCode: number; success: boolean; data: OrganizationBranding; message?: string
}> => {
  try {
    // If no subdomain provided, try to extract from current hostname
    if (!subdomain) {
      const hostname = window.location.hostname;

      // Extract subdomain from hostname
      // Example: "mycompany.basiq360.com" -> "mycompany"
      // Example: "localhost" -> use default or empty
      const parts = hostname.split('.');
      if (parts.length > 2) {
        subdomain = parts[0];
      } else {
        // For localhost or direct domain access, use empty or default
        subdomain = 'localhost';
      }
    }

    logger.info("Calling organization branding API", {
      endpoint: url.API_WEB_ORGANIZATION_BRANDING,
      subdomain,
      hostname: window.location.hostname
    });

    const response: any = await api.create(url.API_WEB_ORGANIZATION_BRANDING, {
      subdomain,
    });

    logger.info("Organization branding API raw response", {
      response,
      hasStatusCode: 'statusCode' in response,
      hasData: 'data' in response,
      responseType: typeof response
    });

    // The axios interceptor returns response.data, which unwraps the API response
    // So if response has statusCode and data, return as-is
    // Otherwise, wrap the response in the expected format
    if ('statusCode' in response && 'data' in response) {
      return response;
    } else {
      // Response is already unwrapped data, wrap it back
      return {
        statusCode: 200,
        success: true,
        data: response,
        message: 'Organization branding retrieved'
      };
    }
  } catch (error: any) {
    logger.error("Error fetching organization branding", {
      error,
      message: error.message,
      status: error.status
    });
    throw error;
  }
};

/**
 * Get System Configuration
 * Fetch system-wide configuration (if available)
 */
export const getSystemConfig = async (): Promise<any> => {
  try {
    // This endpoint may not exist yet, but leaving placeholder
    const response = await api.get("/system/config", {});
    return response;
  } catch (error: any) {
    logger.error("Error fetching system config", error);
    throw error;
  }
};

/**
 * Check System Health
 * Verify if the backend API is reachable
 */
export const checkSystemHealth = async (): Promise<{ status: string; version?: string }> => {
  try {
    const response: any = await api.get("/health", {});
    return response;
  } catch (error: any) {
    logger.error("Error checking system health", error);
    return { status: "down" };
  }
};

/**
 * Get Public Assets
 * Fetch public assets like terms, privacy policy, etc.
 */
export const getPublicDocument = async (documentType: string): Promise<any> => {
  try {
    const response = await api.get(`/public/documents/${documentType}`, {});
    return response;
  } catch (error: any) {
    logger.error(`Error fetching public document (${documentType})`, error);
    throw error;
  }
};

/**
 * Send OTP to Field User
 * Sends OTP to the field user's mobile number for authentication
 *
 * @param mobile - Mobile number to send OTP to
 * @param organizationId - Organization ID
 * @returns Success status and message
 */
export const sendFieldUserOtp = async (params: {
  mobile: string;
  organizationId: number;
}): Promise<{ success: boolean; message?: string; data?: any }> => {
  try {
    const response: any = await api.create(url.API_SEND_FIELD_USER_OTP, params);
    return response;
  } catch (error: any) {
    logger.error("Error sending field user OTP", error);
    throw error;
  }
};

/**
 * Get Menu Data
 * Fetches the menu structure for the logged-in user.
 * This should be called after authentication.
 */
export const getMenuData = async (): Promise<{
  statusCode: number;
  success: boolean;
  data: any;
  message?: string;
}> => {
  try {
    const response: any = await api.get(url.API_WEB_GET_USER_MODULES, {});

    // The axios interceptor unwraps the response, so we need to normalize it
    // If response has modules at root level but no statusCode wrapper, wrap it
    if (response && 'modules' in response && !('statusCode' in response)) {
      return {
        statusCode: 200,
        success: true,
        data: response,
        message: 'User modules retrieved successfully'
      };
    }

    // If response is already properly structured, return as-is
    return response;
  } catch (error) {
    logger.error("Error fetching menu data", error);
    throw error;
  }
};

/**
 * Forgot Password
 * Sends a password reset link to the user's email
 *
 * @param params - Email and organizationId
 * @returns Success status and message
 */
export const forgotPassword = async (params: {
  email: string;
  organizationId: number;
}): Promise<{ statusCode: number; success: boolean; message?: string; data?: any }> => {
  try {
    const response: any = await api.create(url.API_WEB_FORGOT_PASSWORD, params);
    return response;
  } catch (error: any) {
    logger.error("Error requesting password reset", error);
    throw error;
  }
};

/**
 * Verify Reset Token
 * Checks if the password reset token is valid and not expired
 *
 * @param params - Reset token
 * @returns Success status and email if valid
 */
export const verifyResetToken = async (params: {
  token: string;
}): Promise<{ statusCode: number; success: boolean; message?: string; data?: { email?: string } }> => {
  try {
    const response: any = await api.create(url.API_WEB_VERIFY_RESET_TOKEN, params);
    return response;
  } catch (error: any) {
    logger.error("Error verifying reset token", error);
    throw error;
  }
};

/**
 * Reset Password
 * Resets the user's password using the reset token
 *
 * @param params - Token, new password, and confirm password
 * @returns Success status and message
 */
export const resetPassword = async (params: {
  token: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ statusCode: number; success: boolean; message?: string }> => {
  try {
    const response: any = await api.create(url.API_WEB_RESET_PASSWORD, params);
    return response;
  } catch (error: any) {
    logger.error("Error resetting password", error);
    throw error;
  }
};

const systemService = {
  getOrganizationBranding,
  getSystemConfig,
  checkSystemHealth,
  getPublicDocument,
  sendFieldUserOtp,
  getMenuData,
  forgotPassword,
  verifyResetToken,
  resetPassword,
};

export default systemService;
